import i18n from '@translations';

import { AnimatePresence, motion } from 'framer-motion';
import { useMemo } from 'react';

import { Text } from '@components/Text';
import { Dictionaries } from '@enums';
import { Condition } from '@types';
import { getCodeValueFromCatalog, groupConditionsByFieldAndOperator } from '@utils';

interface ConditionsDisplayProps {
  data: Condition[];
  emptyMessage?: string;
}

export default function ConditionsDisplay({ data = [], emptyMessage = 'Always' }: Readonly<ConditionsDisplayProps>) {
  const groupedConditions = useMemo(() => {
    return groupConditionsByFieldAndOperator(data);
  }, [data]);

  const hasConditions = data.length > 0;

  return (
    <AnimatePresence mode="wait">
      {hasConditions ? (
        <motion.div
          className="relative space-y-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {Object.entries(groupedConditions).map(([field, operators]) =>
            Object.entries(operators).map(([operator, values], operatorIndex) => (
              <ConditionGroup
                key={`${field}-${operator}-${operatorIndex}`}
                field={field}
                operator={operator}
                values={values}
              />
            )),
          )}
        </motion.div>
      ) : (
        <motion.div
          className="flex items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Text $level={5} className="text-red-500 font-semibold" $customizeColor>
            {i18n?.t('configuration:tables.content.always') ?? emptyMessage}
          </Text>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const ConditionGroup = ({ field, operator, values }) => {
  return (
    <motion.div
      className="relative left-0.5"
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="flex items-center">
        <div className="flex items-center">
          <Text $customizeColor $level={5} className="font-bold text-gray-700">
            {getCodeValueFromCatalog(Dictionaries.Variable, field)}
          </Text>
          <Text $level={5} className="ml-1.5 text-theme-primary-main" $customizeColor>
            {getCodeValueFromCatalog(Dictionaries.Operator, operator)}
          </Text>
        </div>
      </div>

      {values.length > 0 && (
        <div className="mt-3 relative pl-3 space-y-1.5">
          <div className="absolute left-3 top-2 bottom-2 w-px bg-gray-200"></div>

          <AnimatePresence>
            {values.map((value, valueIndex) => (
              <motion.div
                key={valueIndex}
                className="flex items-center gap-2 w-full"
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -5 }}
                transition={{ duration: 0.15, delay: valueIndex * 0.03 }}
              >
                <div className="w-3 h-px bg-theme-primary-main/20 flex-shrink-0"></div>
                <div className="max-w-full overflow-hidden">
                  <Text $customizeColor $level={5} className="text-gray-600 truncate block">
                    {value ?? ''}
                  </Text>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};
